/**
 * Copyright (c) 2026 上海安能聚创供应链管理有限公司.
 * All rights reserved.
 * 公司内部代码，未经书面授权，不得以任何形式复制、传播或用于与本项目无关的其他用途.
 */
import { fakeAccountLogin, fakeAccessAuth, fakeAccountLogout } from '@/services/api';
import { history } from 'umi';

const getToken = () => sessionStorage.getItem('x-auth');

const getAccess = () => {
  const access = sessionStorage.getItem('access');
  return access ? JSON.parse(access) : '';
};

const getTheme = (type) => {
  const th = localStorage.getItem('theme');
  const bodyEle = document.body;
  if (th) {
    if (bodyEle) {
      bodyEle.className = `theme-${th}`;
    }
    return th;
  }
  localStorage.setItem('theme', type);
  if (bodyEle) {
    bodyEle.className = `theme-${type}`;
  }
  return type;
};
export default {
  namespace: 'global',
  state: {
    isLogin: !!sessionStorage.getItem('access'),
    token: !!getToken(),
    theme: getTheme('dark'),
    access: getAccess(),
    status: undefined,
    message: '',
    loginLoading: false,
  },
  effects: {
    // 登录
    *login({ payload }, { call, put }) {
      yield put({
        type: 'changeSubmitting',
        payload: {
          submitting: true,
          token: null,
        },
      });
      const response = yield call(fakeAccountLogin, payload);
      if ((response && response.code === 1) || response.code === 200) {
        yield sessionStorage.setItem('token', response.token);
        yield sessionStorage.setItem('access', JSON.stringify(response.data));
        history.push('/welcome');
      } else {
        yield sessionStorage.removeItem('token');
        yield sessionStorage.removeItem('access');
      }
      yield put({
        type: 'changeLoginStatus',
        payload: response || {},
      });
    },
    // 退出登录
    *logout(_, { call, put }) {
      // 必须先带着 x-auth 凭据调登出接口，服务端注销会话后返回 logoutUrl；
      // 若提前清除 x-auth，接口按未登录返回 -2，会被请求拦截器整页踢到 401
      const response = yield call(fakeAccountLogout);
      // 接口返回后无论成败，本地凭据与缓存全部清理：
      // x-auth 是 BasicLayout 鉴权 authority 的依据，menus 是菜单缓存，
      // 残留会导致登出后访问页面仍按已登录渲染并发起业务请求，触发 401/500 跳转竞争
      yield sessionStorage.removeItem('token');
      yield sessionStorage.removeItem('access');
      yield sessionStorage.removeItem('permissions');
      yield sessionStorage.removeItem('x-auth');
      yield sessionStorage.removeItem('menus');
      yield localStorage.removeItem('x-auth');
      // response 可能为 undefined（接口挂起时 errorHandler 吞错返回空），安全判断避免 TypeError
      if (response && (response.code === 1 || response.code === 200)) {
        // eslint-disable-next-line
        if (_LOGINMODEL_ === 'cas') {
          // 跳转到cas登录界面
          if (response.logoutUrl && response.logoutUrl !== '') {
            let logoutUrl = response.logoutUrl;
            // 本地开发：服务端生成的 logoutUrl 的 service 指向代理目标（测试环境）且不带
            // refUrl，登出后 CAS 会直接重定向到测试环境；参照 grant 的 loginUrl 结构在
            // service 上追加本地 refUrl，让 CAS 处理完经 shiro-cas 重定向回本地应用
            if (process.env.NODE_ENV === 'development' && logoutUrl.indexOf('refUrl') === -1) {
              const localRefUrl = encodeURIComponent(`${window.location.origin}/route/`);
              logoutUrl = `${logoutUrl}?refUrl=${localRefUrl}`;
            }
            window.location.href = logoutUrl;
          } else {
            history.push('/500');
          }
        } else {
          yield put({
            type: 'changeLoginStatus',
            payload: {
              status: false,
              token: null,
            },
          });
          history.push('/login');
        }
      } else {
        // 退出失败
        history.push('/500');
      }
    },
    //重新登陆
    *caslogin(_, { call, put }) {
      // 调用后台auth接口获取权限数据
      const response = yield call(fakeAccessAuth);
      if (response && response.code && response.code === 1) {
        yield sessionStorage.setItem('access', JSON.stringify(response.data));
        if (response.data && response.data.forceChangePassword) {
          sessionStorage.setItem('_FORCECHANGEPASSWORD_', response.data.forceChangePassword);
        }
        if (response.data && response.data.permissions) {
          let menuPermit = [];
          if (response.data.menus) {
            menuPermit = response.data.menus.map((item) => item.resUrl);
            sessionStorage.setItem('menus', JSON.stringify(response.data.menus));
          }
          const authRoutes = response.data.permissions.concat(menuPermit);
          //将最终合并的authRoutes 数据去重，因为permissions里面有的菜单，menus里也会有
          sessionStorage.setItem('permissions', JSON.stringify([...new Set(authRoutes)]));
          window.location.reload();
        }
      } else {
        yield sessionStorage.removeItem('access');
      }
      yield put({
        type: 'changeLoginLoadingStatus',
        payload: {
          isLogin: false,
        },
      });
      yield put({
        type: 'changeLoginStatus',
        payload: response,
      });
    },
  },
  reducers: {
    update(state, { payload }) {
      return { ...state, ...payload };
    },
    changeTheme(state) {
      const theme = state.theme === 'light' || !state.theme ? 'dark' : 'light';
      localStorage.setItem('theme', theme);
      const bodyEle = document.body;
      if (bodyEle) {
        bodyEle.className = `theme-${theme}`;
      }
      return { ...state, theme };
    },
    changeLoginLoadingStatus(state, { payload }) {
      return {
        ...state,
        isLogin: payload.loginLoading,
      };
    },
    changeLayoutAccess(state, { payload }) {
      sessionStorage.setItem('access', payload);
      return {
        ...state,
        access: payload,
      };
    },
    changeLayoutMenus(state, { payload }) {
      sessionStorage.setItem('menus', payload);
      return {
        ...state,
        menus: payload,
      };
    },
    changeLayoutPermissions(state, { payload }) {
      sessionStorage.setItem('permissions', payload);
      return {
        ...state,
        permissions: payload,
      };
    },
    changeLoginStatus(state, { payload }) {
      return {
        ...state,
        code: payload.code,
        type: payload.type,
        token: payload.token,
        submitting: false,
        access: payload.data,
        message: payload.message,
      };
    },
    changeSubmitting(state, { payload }) {
      return {
        ...state,
        ...payload,
      };
    },
  },
};
